import { supabase, callEdgeFunction } from './supabase';
import type {
  DuplicateRecord,
  SearchGapRecord,
  DuplicateFilters,
  SearchGapFilters,
  DuplicateSummary,
  SearchGapSummary,
  GapType,
  ProfileSource,
  DuplicateMatchType,
} from '@/types/businessControl';

// ============================================================================
// Duplicate Detection
// ============================================================================

interface DuplicateQueryResult {
  id1: string;
  name1: string;
  id2: string;
  name2: string;
  email1: string | null;
  email2: string | null;
  phone1: string | null;
  phone2: string | null;
  match_type: string;
  match_score: number;
  source1: string;
  source2: string;
  status1: string;
  status2: string;
  created_at1: string;
  created_at2: string;
}

export async function fetchDuplicates(
  filters: DuplicateFilters = {}
): Promise<{ duplicates: DuplicateRecord[]; summary: DuplicateSummary }> {
  // Build the SQL for finding duplicates
  // Using raw SQL via RPC for complex join queries
  const { data, error } = await supabase.rpc('find_duplicate_business_profiles', {
    p_match_type: filters.matchType === 'all' ? null : filters.matchType,
    p_source: filters.profileSource === 'all' ? null : filters.profileSource,
    p_status: filters.status === 'all' ? null : filters.status,
    p_min_score: filters.minScore ?? 0.7,
  });

  if (error) {
    // Fallback to direct query if RPC doesn't exist
    if (error.code === '42883') {
      console.warn('find_duplicate_business_profiles RPC not found, using fallback query');
      return fetchDuplicatesFallback(filters);
    }
    throw error;
  }

  const duplicates: DuplicateRecord[] = (data || []).map((row: DuplicateQueryResult) => ({
    id1: row.id1,
    name1: row.name1,
    id2: row.id2,
    name2: row.name2,
    email1: row.email1,
    email2: row.email2,
    phone1: row.phone1,
    phone2: row.phone2,
    matchType: row.match_type as DuplicateMatchType,
    matchScore: row.match_score,
    source1: row.source1 as ProfileSource,
    source2: row.source2 as ProfileSource,
    status1: row.status1,
    status2: row.status2,
    createdAt1: row.created_at1,
    createdAt2: row.created_at2,
  }));

  // Calculate summary
  const summary = calculateDuplicateSummary(duplicates);

  return { duplicates, summary };
}

// Fallback query if RPC doesn't exist
async function fetchDuplicatesFallback(
  filters: DuplicateFilters
): Promise<{ duplicates: DuplicateRecord[]; summary: DuplicateSummary }> {
  // Get email duplicates
  const { data: emailDupes, error: emailError } = await supabase
    .from('business_profiles')
    .select(`
      business_id,
      business_name,
      contact_email,
      phone_number,
      is_active,
      business_status,
      created_at,
      preset_created_by,
      user_id
    `)
    .not('contact_email', 'is', null)
    .order('contact_email');

  if (emailError) throw emailError;

  // Find duplicates by grouping
  const duplicates: DuplicateRecord[] = [];
  const emailGroups = new Map<string, typeof emailDupes>();

  for (const profile of emailDupes || []) {
    if (!profile.contact_email) continue;
    const email = profile.contact_email.toLowerCase();
    if (!emailGroups.has(email)) {
      emailGroups.set(email, []);
    }
    emailGroups.get(email)!.push(profile);
  }

  // Convert groups to pairs
  for (const [, profiles] of emailGroups) {
    if (profiles.length < 2) continue;

    for (let i = 0; i < profiles.length - 1; i++) {
      for (let j = i + 1; j < profiles.length; j++) {
        const p1 = profiles[i];
        const p2 = profiles[j];

        const source1 = getProfileSource(p1);
        const source2 = getProfileSource(p2);

        // Apply filters
        if (filters.profileSource && filters.profileSource !== 'all') {
          if (source1 !== filters.profileSource && source2 !== filters.profileSource) continue;
        }

        if (filters.status && filters.status !== 'all') {
          const isActive = filters.status === 'active';
          if (p1.is_active !== isActive && p2.is_active !== isActive) continue;
        }

        duplicates.push({
          id1: p1.business_id,
          name1: p1.business_name,
          id2: p2.business_id,
          name2: p2.business_name,
          email1: p1.contact_email,
          email2: p2.contact_email,
          phone1: p1.phone_number,
          phone2: p2.phone_number,
          matchType: 'email',
          matchScore: 1.0,
          source1,
          source2,
          status1: p1.is_active ? 'Active' : 'Inactive',
          status2: p2.is_active ? 'Active' : 'Inactive',
          createdAt1: p1.created_at,
          createdAt2: p2.created_at,
        });
      }
    }
  }

  const summary = calculateDuplicateSummary(duplicates);

  return { duplicates, summary };
}

function getProfileSource(profile: { preset_created_by?: string | null; user_id?: string | null }): ProfileSource {
  if (profile.preset_created_by) return 'preset';
  if (profile.user_id) return 'consumer';
  return 'unknown';
}

function calculateDuplicateSummary(duplicates: DuplicateRecord[]): DuplicateSummary {
  const byMatchType: Record<DuplicateMatchType, number> = {
    email: 0,
    name: 0,
    phone: 0,
    address: 0,
  };

  const bySource: Record<ProfileSource, number> = {
    consumer: 0,
    preset: 0,
    unknown: 0,
  };

  for (const dup of duplicates) {
    byMatchType[dup.matchType]++;
    bySource[dup.source1]++;
    if (dup.source2 !== dup.source1) {
      bySource[dup.source2]++;
    }
  }

  return {
    total: duplicates.length,
    byMatchType,
    bySource,
  };
}

// ============================================================================
// Search Readiness Gaps
// ============================================================================

interface SearchGapQueryResult {
  business_id: string;
  business_name: string;
  gap_type: string;
  root_cause: string;
  profile_source: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  business_status: string;
  embedding_generated_at: string | null;
  search_doc_updated_at: string | null;
  has_coordinates: boolean;
  has_coverage: boolean;
  location_type: string[] | null;
}

export async function fetchSearchGaps(
  filters: SearchGapFilters = {}
): Promise<{ gaps: SearchGapRecord[]; summary: SearchGapSummary }> {
  // Try RPC first, fall back to direct query
  const { data, error } = await supabase.rpc('find_search_readiness_gaps', {
    p_gap_type: filters.gapType === 'all' ? null : filters.gapType,
    p_source: filters.profileSource === 'all' ? null : filters.profileSource,
    p_status: filters.status === 'all' ? null : filters.status,
    p_created_after: filters.createdAfter || null,
    p_created_before: filters.createdBefore || null,
  });

  if (error) {
    // Fallback if RPC doesn't exist
    if (error.code === '42883') {
      console.warn('find_search_readiness_gaps RPC not found, using fallback query');
      return fetchSearchGapsFallback(filters);
    }
    throw error;
  }

  const gaps: SearchGapRecord[] = (data || []).map((row: SearchGapQueryResult) => ({
    businessId: row.business_id,
    businessName: row.business_name,
    gapType: row.gap_type as GapType,
    rootCause: row.root_cause,
    profileSource: row.profile_source as ProfileSource,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    isActive: row.is_active,
    businessStatus: row.business_status,
    embeddingGeneratedAt: row.embedding_generated_at,
    searchDocUpdatedAt: row.search_doc_updated_at,
    hasCoordinates: row.has_coordinates,
    hasCoverage: row.has_coverage,
    locationType: row.location_type,
  }));

  const summary = calculateSearchGapSummary(gaps);

  return { gaps, summary };
}

async function fetchSearchGapsFallback(
  filters: SearchGapFilters
): Promise<{ gaps: SearchGapRecord[]; summary: SearchGapSummary }> {
  // Get business profiles for search readiness monitoring
  // NOTE: Monitor ALL profiles (both active and inactive) to catch issues before activation
  // is_active and status are synced via database trigger (trg_sync_is_active_status)
  let query = supabase
    .from('business_profiles')
    .select(`
      business_id,
      business_name,
      is_active,
      status,
      business_status,
      created_at,
      updated_at,
      location_type,
      latitude,
      longitude,
      embedding,
      embedding_generated_at,
      coverage_type,
      coverage_radius,
      preset_created_by,
      user_id
    `);

  // Apply status filter if specified (default: show all to catch issues early)
  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters.createdAfter) {
    query = query.gte('created_at', filters.createdAfter);
  }
  if (filters.createdBefore) {
    query = query.lte('created_at', filters.createdBefore);
  }

  const { data: profiles, error: profileError } = await query;

  if (profileError) throw profileError;

  // Get search docs
  const { data: searchDocs, error: searchDocError } = await supabase
    .from('business_search_docs')
    .select('business_id, updated_at');

  if (searchDocError) throw searchDocError;

  // Get coverage
  const { data: coverage, error: coverageError } = await supabase
    .from('business_coverage')
    .select('business_id');

  if (coverageError) throw coverageError;

  const searchDocMap = new Map(searchDocs?.map(sd => [sd.business_id, sd]) || []);
  const coverageSet = new Set(coverage?.map(c => c.business_id) || []);

  const gaps: SearchGapRecord[] = [];

  for (const profile of profiles || []) {
    const source = getProfileSource(profile);

    // Apply source filter
    if (filters.profileSource && filters.profileSource !== 'all' && source !== filters.profileSource) {
      continue;
    }

    const searchDoc = searchDocMap.get(profile.business_id);
    const hasCoverage = coverageSet.has(profile.business_id);
    const hasCoordinates = profile.latitude != null && profile.longitude != null;
    const locationTypes = Array.isArray(profile.location_type) ? profile.location_type : [];
    const needsCoordinates = locationTypes.some((lt: string) =>
      ['storefront', 'office'].includes(lt?.toLowerCase?.() || '')
    );
    const needsCoverage = locationTypes.some((lt: string) =>
      lt?.toLowerCase?.().includes('outbound')
    ) && (profile.coverage_type || profile.coverage_radius);

    // Check for gaps
    let gapType: GapType | null = null;
    let rootCause = '';

    if (!profile.embedding) {
      gapType = 'missing_embedding';
      rootCause = 'Embedding trigger may have failed or profile is too new';
    } else if (!searchDoc) {
      gapType = 'missing_search_doc';
      rootCause = 'Profile not in v_active_businesses view or refresh never called';
    } else if (needsCoordinates && !hasCoordinates) {
      gapType = 'missing_coordinates';
      rootCause = 'Geocoding failed or no valid address provided';
    } else if (needsCoverage && !hasCoverage) {
      gapType = 'missing_coverage';
      rootCause = 'Coverage polygon build never triggered';
    } else if (profile.embedding_generated_at && profile.updated_at &&
               new Date(profile.embedding_generated_at) < new Date(profile.updated_at)) {
      gapType = 'stale_embedding';
      rootCause = 'Profile updated after embedding was generated';
    } else if (searchDoc && searchDoc.updated_at &&
               new Date(searchDoc.updated_at) < new Date(profile.updated_at)) {
      gapType = 'stale_search_doc';
      rootCause = 'Search document not refreshed after profile update';
    }

    if (gapType) {
      // Apply gap type filter
      if (filters.gapType && filters.gapType !== 'all' && gapType !== filters.gapType) {
        continue;
      }

      gaps.push({
        businessId: profile.business_id,
        businessName: profile.business_name,
        gapType,
        rootCause,
        profileSource: source,
        createdAt: profile.created_at,
        updatedAt: profile.updated_at,
        isActive: profile.is_active,
        businessStatus: profile.business_status,
        embeddingGeneratedAt: profile.embedding_generated_at,
        searchDocUpdatedAt: searchDoc?.updated_at || null,
        hasCoordinates,
        hasCoverage,
        locationType: locationTypes,
      });
    }
  }

  const summary = calculateSearchGapSummary(gaps);

  return { gaps, summary };
}

function calculateSearchGapSummary(gaps: SearchGapRecord[]): SearchGapSummary {
  const byGapType: Record<GapType, number> = {
    missing_embedding: 0,
    missing_search_doc: 0,
    missing_coordinates: 0,
    missing_coverage: 0,
    stale_embedding: 0,
    stale_search_doc: 0,
  };

  const bySource: Record<ProfileSource, number> = {
    consumer: 0,
    preset: 0,
    unknown: 0,
  };

  for (const gap of gaps) {
    byGapType[gap.gapType]++;
    bySource[gap.profileSource]++;
  }

  return {
    total: gaps.length,
    byGapType,
    bySource,
  };
}

// ============================================================================
// Fix Actions
// ============================================================================

export async function fixSearchGap(
  businessId: string,
  gapType: GapType
): Promise<{ success: boolean; message: string }> {
  try {
    switch (gapType) {
      case 'missing_embedding':
      case 'stale_embedding':
        // Queue embedding generation
        await supabase.from('embedding_generation_queue').upsert({
          business_id: businessId,
          status: 'pending',
          created_at: new Date().toISOString(),
        });
        return { success: true, message: 'Embedding generation queued' };

      case 'missing_search_doc':
      case 'stale_search_doc':
        // Refresh search document
        const { error: searchDocError } = await supabase.rpc('refresh_business_search_doc', {
          p_business_id: businessId,
        });
        if (searchDocError) throw searchDocError;
        return { success: true, message: 'Search document refreshed' };

      case 'missing_coordinates':
        // Trigger geocoding via edge function
        await callEdgeFunction('admin-geocode-business', { business_id: businessId });
        return { success: true, message: 'Geocoding triggered' };

      case 'missing_coverage':
        // Build coverage polygon
        await callEdgeFunction('build-coverage-polygons', { business_id: businessId });
        return { success: true, message: 'Coverage polygon build triggered' };

      default:
        return { success: false, message: 'Unknown gap type' };
    }
  } catch (error) {
    console.error('Failed to fix search gap:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function bulkFixGaps(
  businessIds: string[],
  gapType: GapType
): Promise<{ successCount: number; failCount: number; errors: string[] }> {
  const errors: string[] = [];
  let successCount = 0;
  let failCount = 0;

  // Process in batches to avoid overwhelming the system
  const batchSize = 10;
  for (let i = 0; i < businessIds.length; i += batchSize) {
    const batch = businessIds.slice(i, i + batchSize);

    const results = await Promise.allSettled(
      batch.map(id => fixSearchGap(id, gapType))
    );

    for (let j = 0; j < results.length; j++) {
      const result = results[j];
      if (result.status === 'fulfilled' && result.value.success) {
        successCount++;
      } else {
        failCount++;
        const errorMsg = result.status === 'rejected'
          ? result.reason?.message || 'Unknown error'
          : result.value.message;
        errors.push(`${batch[j]}: ${errorMsg}`);
      }
    }
  }

  return { successCount, failCount, errors };
}

// ============================================================================
// Duplicate Actions
// ============================================================================

export async function dismissDuplicate(
  id1: string,
  id2: string
): Promise<{ success: boolean }> {
  // Store dismissed duplicates to avoid re-flagging
  const { error } = await supabase.from('dismissed_duplicates').upsert({
    business_id_1: id1 < id2 ? id1 : id2,
    business_id_2: id1 < id2 ? id2 : id1,
    dismissed_at: new Date().toISOString(),
  });

  if (error) {
    // If table doesn't exist, just return success (dismissal is optional)
    if (error.code === '42P01') {
      console.warn('dismissed_duplicates table not found, skipping dismissal tracking');
      return { success: true };
    }
    throw error;
  }

  return { success: true };
}

export async function mergeDuplicates(
  keepId: string,
  mergeId: string
): Promise<{ success: boolean; message: string }> {
  // This is a complex operation that should be handled by an edge function
  // to properly merge all related data (connections, recommendations, etc.)
  try {
    await callEdgeFunction('admin-merge-business-profiles', {
      keep_business_id: keepId,
      merge_business_id: mergeId,
    });
    return { success: true, message: 'Profiles merged successfully' };
  } catch (error) {
    console.error('Failed to merge profiles:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Merge failed',
    };
  }
}

/**
 * Resolve a duplicate pair by keeping one profile and deleting the other,
 * or deleting both profiles.
 *
 * @param id1 - First profile ID (kept for keep_first, deleted for keep_second, deleted for delete_both)
 * @param id2 - Second profile ID (deleted for keep_first, kept for keep_second, deleted for delete_both)
 * @param action - The resolution action to take
 */
export async function resolveDuplicate(
  id1: string | undefined,
  id2: string | undefined,
  action: 'keep_first' | 'keep_second' | 'delete_both'
): Promise<{ success: boolean; message: string }> {
  try {
    if (!id1 || !id2) {
      throw new Error('Both profile IDs are required');
    }

    if (action === 'delete_both') {
      // Delete both profiles
      const { error: deleteError1 } = await supabase
        .from('business_profiles')
        .update({
          status: 'deleted',
          is_active: false,
          is_searchable: false,
          updated_at: new Date().toISOString(),
        })
        .eq('business_id', id1);

      if (deleteError1) {
        throw new Error(`Failed to delete first profile: ${deleteError1.message}`);
      }

      const { error: deleteError2 } = await supabase
        .from('business_profiles')
        .update({
          status: 'deleted',
          is_active: false,
          is_searchable: false,
          updated_at: new Date().toISOString(),
        })
        .eq('business_id', id2);

      if (deleteError2) {
        throw new Error(`Failed to delete second profile: ${deleteError2.message}`);
      }

      // Dismiss this duplicate pair
      await dismissDuplicate(id1, id2);

      return {
        success: true,
        message: 'Both profiles deleted',
      };
    }

    // For keep_first or keep_second, delete the other profile
    const keepId = action === 'keep_first' ? id1 : id2;
    const deleteId = action === 'keep_first' ? id2 : id1;

    // Soft delete the profile to be removed (set status to 'deleted')
    // This preserves data for audit purposes while removing from active use
    const { error: deleteError } = await supabase
      .from('business_profiles')
      .update({
        status: 'deleted',
        is_active: false,
        is_searchable: false,
        updated_at: new Date().toISOString(),
      })
      .eq('business_id', deleteId);

    if (deleteError) {
      throw new Error(`Failed to delete profile: ${deleteError.message}`);
    }

    // Also dismiss this duplicate pair so it doesn't reappear
    await dismissDuplicate(keepId, deleteId);

    return {
      success: true,
      message: `Profile deleted. Kept: ${keepId.slice(0, 8)}...`,
    };
  } catch (error) {
    console.error('Failed to resolve duplicate:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Resolution failed',
    };
  }
}

/**
 * Delete a business profile (soft delete - sets status to 'deleted')
 */
export async function deleteBusinessProfile(
  businessId: string
): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('business_profiles')
      .update({
        status: 'deleted',
        is_active: false,
        is_searchable: false,
        updated_at: new Date().toISOString(),
      })
      .eq('business_id', businessId);

    if (error) {
      throw new Error(`Failed to delete profile: ${error.message}`);
    }

    return { success: true, message: 'Profile deleted successfully' };
  } catch (error) {
    console.error('Failed to delete profile:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Delete failed',
    };
  }
}
